import { Controller } from "@hotwired/stimulus"

// STAGE_COLORS is Chart.defaults.global.colors[0..6] expanded to rgba - what this doughnut has
// always actually drawn with. An older 8-entry palette existed but never reached the chart,
// because the canvas attribute carrying it was misspelled and never bound.
//
// chart_defaults.js also sets Chart.defaults.global.legend.display = false app-wide, which is why
// no chart here sets its own `legend` option.
const STAGE_LABELS = ["Sent", "Redeemed", "Shipped", "Canceled", "Bounced", "Declined", "Delivered"]
const STAGE_COLORS = [
  "rgba(151,187,205,1)", "rgba(220,220,220,1)", "rgba(247,70,74,1)", "rgba(70,191,189,1)",
  "rgba(253,180,92,1)", "rgba(148,159,177,1)", "rgba(77,83,96,1)"
]
const BUCKETS = [25, 50, 75, 100, 150, 250, 500]
const BAR_BACKGROUND = "rgba(151,187,205,0.2)"
const BAR_BORDER = "rgba(151,187,205,1)"
const LABEL_LENGTH = 18

export default class extends Controller {
  static get targets() {
    return ["stagesCanvas", "bucketCountsCanvas", "bucketCostsCanvas", "galleriesCanvas", "selectionsCanvas"]
  }

  static get values() { return { billing: Object } }

  connect() {
    this.charts = []
    if (this.hasStagesCanvasTarget) { this.charts.push(this.buildStages()) }
    if (this.hasBucketCountsCanvasTarget) { this.charts.push(this.buildBucketCounts()) }
    if (this.hasBucketCostsCanvasTarget) { this.charts.push(this.buildBucketCosts()) }
    if (this.hasGalleriesCanvasTarget) { this.charts.push(this.buildTopChart(this.galleriesCanvasTarget, "top_gallery_")) }
    if (this.hasSelectionsCanvasTarget) { this.charts.push(this.buildTopChart(this.selectionsCanvasTarget, "top_selection_")) }
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy())
    this.charts = []
  }

  buildStages() {
    const billing = this.billingValue
    return new Chart(this.stagesCanvasTarget, {
      type: "doughnut",
      data: {
        labels: STAGE_LABELS,
        datasets: [{
          data: [
            billing.sent_at_count, billing.redeemed_at_count, billing.shipped_at_count,
            billing.canceled_at_count, billing.bounced_at_count, billing.declined_at_count,
            billing.delivered_at_count
          ],
          backgroundColor: STAGE_COLORS
        }]
      },
      options: { responsive: true }
    })
  }

  buildBucketCounts() {
    const billing = this.billingValue
    return this.buildBarChart(this.bucketCountsCanvasTarget, BUCKETS.map((n) => billing["count_" + n]))
  }

  buildBucketCosts() {
    const billing = this.billingValue
    return this.buildBarChart(this.bucketCostsCanvasTarget, BUCKETS.map((n) => billing["cost_" + n + "_cents"] / 100.0))
  }

  buildBarChart(canvas, data, labels) {
    return new Chart(canvas, {
      type: "bar",
      data: {
        labels: labels || BUCKETS.map((n) => "$" + n),
        datasets: [{ data: data, backgroundColor: BAR_BACKGROUND, borderColor: BAR_BORDER }]
      },
      options: { responsive: true }
    })
  }

  buildTopChart(canvas, prefix) {
    const billing = this.billingValue
    const indexes = [1, 2, 3, 4, 5]
    const data = indexes.map((i) => billing[prefix + i + "_count"])
    // Known quirk, left as-is: a blank name is dropped from the array rather than becoming a null
    // label, so the surviving labels shift onto the wrong bars by index.
    const labels = indexes
      .map((i) => this.truncated(billing[prefix + i + "_name"]))
      .filter((label) => label !== null && label !== undefined)
    return this.buildBarChart(canvas, data, labels)
  }

  truncated(name) {
    if (!name) { return name }
    return name.length > LABEL_LENGTH ? name.substring(0, LABEL_LENGTH) + "..." : name.substring(0, LABEL_LENGTH)
  }
}
